import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import type { Company, Client, Project, Budget, Expense, ProjectPhoto, ProjectDocument, PortfolioConfig, CompanyState, CompanyContextValue } from '../types';
import { useAuth } from './AuthContext';
import { ensurePortfolio, savePortfolio } from '../services/portfolio';

const CompanyContext = createContext<CompanyContextValue | undefined>(undefined);

const STORAGE_PREFIX = 'reformall_';
const COMPANIES_KEY = 'reformall_all_companies';

const getStorageKey = (companyId: string, entity: string) => `${STORAGE_PREFIX}${companyId}_${entity}`;

const loadAllCompanies = (): Company[] => {
  try {
    const data = localStorage.getItem(COMPANIES_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
};

const saveAllCompanies = (list: Company[]) => {
  localStorage.setItem(COMPANIES_KEY, JSON.stringify(list));
};

const loadFromStorage = <T,>(key: string, defaultValue: T): T => {
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : defaultValue;
  } catch {
    return defaultValue;
  }
};

const saveToStorage = <T,>(key: string, data: T) => {
  localStorage.setItem(key, JSON.stringify(data));
};

const generateId = () => crypto.randomUUID();

export function CompanyProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [state, setState] = useState<CompanyState>({
    company: null,
    companies: loadAllCompanies(),
    portfolio: null,
    clients: [],
    projects: [],
    budgets: [],
    expenses: [],
    photos: [],
    documents: [],
    isLoading: true,
    refresh: async () => {},
    deleteCompany: async () => {},
  });

  const refresh = useCallback(async () => {
    if (!user?.companyId) {
      setState(prev => ({ ...prev, isLoading: false }));
      return;
    }

    setState(prev => ({ ...prev, isLoading: true }));

    // Simular carga
    await new Promise(r => setTimeout(r, 300));

    const companyId = user.companyId;
    const company = loadFromStorage<Company | null>(getStorageKey(companyId, 'company'), null);
    const clients = loadFromStorage<Client[]>(getStorageKey(companyId, 'clients'), []);
    const projects = loadFromStorage<Project[]>(getStorageKey(companyId, 'projects'), []);
    const budgets = loadFromStorage<Budget[]>(getStorageKey(companyId, 'budgets'), []);
    const expenses = loadFromStorage<Expense[]>(getStorageKey(companyId, 'expenses'), []);
    const photos = loadFromStorage<ProjectPhoto[]>(getStorageKey(companyId, 'photos'), []);
    const documents = loadFromStorage<ProjectDocument[]>(getStorageKey(companyId, 'documents'), []);
    const portfolio = company ? ensurePortfolio(company) : null;

    setState({
      company,
      companies: loadAllCompanies(),
      portfolio,
      clients,
      projects,
      budgets,
      expenses,
      photos,
      documents,
      isLoading: false,
      refresh: async () => {},
      deleteCompany: async () => {},
    });
  }, [user?.companyId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Company actions
  const createCompany = useCallback(async (data: Partial<Company>, ownerId: string) => {
    const newCompany: Company = {
      id: generateId(),
      name: data.name || '',
      fiscalName: data.fiscalName,
      cif: data.cif,
      address: data.address,
      phone: data.phone,
      email: data.email,
      website: data.website,
      logo: data.logo,
      vatRate: data.vatRate || 21,
      paymentTerms: data.paymentTerms,
      warrantyClauses: data.warrantyClauses,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      ownerId,
    };

    saveToStorage(getStorageKey(newCompany.id, 'company'), newCompany);
    const all = loadAllCompanies();
    if (!all.some(c => c.id === newCompany.id)) {
      saveAllCompanies([...all, newCompany]);
    }
    return newCompany;
  }, []);

  const updateCompany = useCallback(async (updates: Partial<Company>) => {
    if (!state.company) return;
    const updated = { ...state.company, ...updates, updatedAt: new Date().toISOString() };
    saveToStorage(getStorageKey(updated.id, 'company'), updated);
    setState(prev => ({ ...prev, company: updated }));
  }, [state.company]);

  const updatePortfolio = useCallback(async (updates: Partial<PortfolioConfig>) => {
    if (!state.company) return null;
    const current = state.portfolio ?? ensurePortfolio(state.company);
    const updated: PortfolioConfig = {
      ...current,
      ...updates,
      companyId: state.company.id,
      updatedAt: new Date().toISOString(),
    };
    savePortfolio(updated);
    setState(prev => ({ ...prev, portfolio: updated }));
    return updated;
  }, [state.company, state.portfolio]);

  // Client actions
  const createClient = useCallback(async (data: Omit<Client, 'id' | 'companyId' | 'createdAt' | 'updatedAt'>) => {
    if (!user?.companyId) throw new Error('No company');
    const newClient: Client = {
      ...data,
      id: generateId(),
      companyId: user.companyId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    const updated = [...state.clients, newClient];
    saveToStorage(getStorageKey(user.companyId, 'clients'), updated);
    setState(prev => ({ ...prev, clients: updated }));
    return newClient;
  }, [user?.companyId, state.clients]);

  const updateClient = useCallback(async (id: string, updates: Partial<Client>) => {
    if (!user?.companyId) return;
    const updated = state.clients.map(c => c.id === id ? { ...c, ...updates, updatedAt: new Date().toISOString() } : c);
    saveToStorage(getStorageKey(user.companyId, 'clients'), updated);
    setState(prev => ({ ...prev, clients: updated }));
  }, [user?.companyId, state.clients]);

  const deleteClient = useCallback(async (id: string) => {
    if (!user?.companyId) return;
    const updated = state.clients.filter(c => c.id !== id);
    saveToStorage(getStorageKey(user.companyId, 'clients'), updated);
    setState(prev => ({ ...prev, clients: updated }));
  }, [user?.companyId, state.clients]);

  // Project actions
  const createProject = useCallback(async (data: Omit<Project, 'id' | 'companyId' | 'createdAt' | 'updatedAt'>) => {
    if (!user?.companyId) throw new Error('No company');
    const newProject: Project = {
      ...data,
      id: generateId(),
      companyId: user.companyId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    const updated = [...state.projects, newProject];
    saveToStorage(getStorageKey(user.companyId, 'projects'), updated);
    setState(prev => ({ ...prev, projects: updated }));
    return newProject;
  }, [user?.companyId, state.projects]);

  const updateProject = useCallback(async (id: string, updates: Partial<Project>) => {
    if (!user?.companyId) return;
    const updated = state.projects.map(p => p.id === id ? { ...p, ...updates, updatedAt: new Date().toISOString() } : p);
    saveToStorage(getStorageKey(user.companyId, 'projects'), updated);
    setState(prev => ({ ...prev, projects: updated }));
  }, [user?.companyId, state.projects]);

  const deleteProject = useCallback(async (id: string) => {
    if (!user?.companyId) return;
    const updated = state.projects.filter(p => p.id !== id);
    saveToStorage(getStorageKey(user.companyId, 'projects'), updated);
    setState(prev => ({ ...prev, projects: updated }));
  }, [user?.companyId, state.projects]);

  // Budget actions
  const createBudget = useCallback(async (data: Omit<Budget, 'id' | 'companyId' | 'createdAt' | 'updatedAt' | 'number' | 'subtotal' | 'vatAmount' | 'total'>) => {
    if (!user?.companyId) throw new Error('No company');
    const year = new Date().getFullYear();
    const count = state.budgets.filter(b => b.number.startsWith(`${year}-`)).length + 1;
    const number = `${year}-${count.toString().padStart(4, '0')}`;

    const subtotal = [...data.items, ...data.extraItems].reduce((sum, i) => sum + i.quantity * i.unitPrice, 0);
    const vatAmount = subtotal * data.vatRate / 100;

    const newBudget: Budget = {
      ...data,
      id: generateId(),
      companyId: user.companyId,
      number,
      subtotal,
      vatAmount,
      total: subtotal + vatAmount,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    const updated = [...state.budgets, newBudget];
    saveToStorage(getStorageKey(user.companyId, 'budgets'), updated);
    setState(prev => ({ ...prev, budgets: updated }));
    return newBudget;
  }, [user?.companyId, state.budgets]);

  const updateBudget = useCallback(async (id: string, updates: Partial<Budget>) => {
    if (!user?.companyId) return;
    const budget = state.budgets.find(b => b.id === id);
    if (!budget) return;

    const items = updates.items ?? budget.items;
    const extraItems = updates.extraItems ?? budget.extraItems;
    const vatRate = updates.vatRate ?? budget.vatRate;
    const subtotal = [...items, ...extraItems].reduce((sum, i) => sum + i.quantity * i.unitPrice, 0);
    const vatAmount = subtotal * vatRate / 100;

    const updated = state.budgets.map(b =>
      b.id === id
        ? { ...b, ...updates, subtotal, vatAmount, total: subtotal + vatAmount, updatedAt: new Date().toISOString() }
        : b
    );
    saveToStorage(getStorageKey(user.companyId, 'budgets'), updated);
    setState(prev => ({ ...prev, budgets: updated }));
  }, [user?.companyId, state.budgets]);

  const deleteBudget = useCallback(async (id: string) => {
    if (!user?.companyId) return;
    const updated = state.budgets.filter(b => b.id !== id);
    saveToStorage(getStorageKey(user.companyId, 'budgets'), updated);
    setState(prev => ({ ...prev, budgets: updated }));
  }, [user?.companyId, state.budgets]);

  // Expense actions
  const createExpense = useCallback(async (data: Omit<Expense, 'id' | 'companyId' | 'createdAt' | 'updatedAt' | 'vatAmount' | 'total'>) => {
    if (!user?.companyId) throw new Error('No company');
    const vatAmount = data.amount * data.vatRate / 100;
    const newExpense: Expense = {
      ...data,
      id: generateId(),
      companyId: user.companyId,
      vatAmount,
      total: data.amount + vatAmount,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    const updated = [...state.expenses, newExpense];
    saveToStorage(getStorageKey(user.companyId, 'expenses'), updated);
    setState(prev => ({ ...prev, expenses: updated }));
    return newExpense;
  }, [user?.companyId, state.expenses]);

  const updateExpense = useCallback(async (id: string, updates: Partial<Expense>) => {
    if (!user?.companyId) return;
    const expense = state.expenses.find(e => e.id === id);
    if (!expense) return;

    const amount = updates.amount ?? expense.amount;
    const vatRate = updates.vatRate ?? expense.vatRate;
    const vatAmount = amount * vatRate / 100;

    const updated = state.expenses.map(e =>
      e.id === id
        ? { ...e, ...updates, vatAmount, total: amount + vatAmount, updatedAt: new Date().toISOString() }
        : e
    );
    saveToStorage(getStorageKey(user.companyId, 'expenses'), updated);
    setState(prev => ({ ...prev, expenses: updated }));
  }, [user?.companyId, state.expenses]);

  const deleteExpense = useCallback(async (id: string) => {
    if (!user?.companyId) return;
    const updated = state.expenses.filter(e => e.id !== id);
    saveToStorage(getStorageKey(user.companyId, 'expenses'), updated);
    setState(prev => ({ ...prev, expenses: updated }));
  }, [user?.companyId, state.expenses]);

  // Project photo actions (galería de evolución)
  const addProjectPhoto = useCallback(async (data: Omit<ProjectPhoto, 'id' | 'companyId' | 'createdAt'>) => {
    if (!user?.companyId) throw new Error('No company');
    const newPhoto: ProjectPhoto = {
      ...data,
      id: generateId(),
      companyId: user.companyId,
      createdAt: new Date().toISOString(),
    };
    const updated = [...state.photos, newPhoto];
    saveToStorage(getStorageKey(user.companyId, 'photos'), updated);
    setState(prev => ({ ...prev, photos: updated }));
    return newPhoto;
  }, [user?.companyId, state.photos]);

  const deleteProjectPhoto = useCallback(async (id: string) => {
    if (!user?.companyId) return;
    const updated = state.photos.filter(p => p.id !== id);
    saveToStorage(getStorageKey(user.companyId, 'photos'), updated);
    setState(prev => ({ ...prev, photos: updated }));
  }, [user?.companyId, state.photos]);

  // Project document actions (documentos, tickets, facturas, planos…)
  const addProjectDocument = useCallback(async (data: Omit<ProjectDocument, 'id' | 'companyId' | 'createdAt'>) => {
    if (!user?.companyId) throw new Error('No company');
    const newDoc: ProjectDocument = {
      ...data,
      id: generateId(),
      companyId: user.companyId,
      createdAt: new Date().toISOString(),
    };
    const updated = [...state.documents, newDoc];
    saveToStorage(getStorageKey(user.companyId, 'documents'), updated);
    setState(prev => ({ ...prev, documents: updated }));
    return newDoc;
  }, [user?.companyId, state.documents]);

  const deleteProjectDocument = useCallback(async (id: string) => {
    if (!user?.companyId) return;
    const updated = state.documents.filter(d => d.id !== id);
    saveToStorage(getStorageKey(user.companyId, 'documents'), updated);
    setState(prev => ({ ...prev, documents: updated }));
  }, [user?.companyId, state.documents]);

  const deleteCompany = useCallback(async (id: string) => {
    const entities = ['company', 'clients', 'projects', 'budgets', 'expenses', 'photos', 'documents', 'portfolio'];
    entities.forEach(entity => localStorage.removeItem(getStorageKey(id, entity)));
    const remaining = loadAllCompanies().filter(c => c.id !== id);
    saveAllCompanies(remaining);
    setState(prev => ({
      ...prev,
      companies: remaining,
      ...(user?.companyId === id ? { company: null, portfolio: null, clients: [], projects: [], budgets: [], expenses: [], photos: [], documents: [] } : {}),
    }));
  }, [user?.companyId]);

  const value = {
    ...state,
    refresh,
    deleteCompany,
    createCompany,
    updateCompany,
    updatePortfolio,
    createClient,
    updateClient,
    deleteClient,
    createProject,
    updateProject,
    deleteProject,
    createBudget,
    updateBudget,
    deleteBudget,
    createExpense,
    updateExpense,
    deleteExpense,
    addProjectPhoto,
    deleteProjectPhoto,
    addProjectDocument,
    deleteProjectDocument,
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany() {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
}